import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../i18n/LanguageContext';
import { projects } from '../data/projects';

const { FiArrowUpRight } = FiIcons;

gsap.registerPlugin(ScrollTrigger);

const ExperienceSection = () => {
  const sectionRef = useRef(null);
  const { t, localize } = useLanguage();

  useEffect(() => {
    const context = gsap.context(() => {
      gsap.from('.experience-item', {
        y: 20,
        opacity: 0,
        duration: 0.6,
        stagger: 0.1,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 75%',
          toggleActions: 'play none none reverse'
        }
      });
    }, sectionRef);

    return () => context.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="experience"
      className="relative min-h-screen w-full bg-[#050505] py-20 px-6 md:px-12 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
          <h2 className="text-4xl md:text-6xl font-light text-white tracking-tight">
            {t('heading')}
          </h2>
          <LanguageSwitcher />
        </div>

        <div className="mb-8 pl-4 md:pl-12">
          <div className="flex flex-col md:flex-row md:items-baseline gap-4 mb-4">
            <h3 className="text-2xl md:text-3xl font-bold text-white tracking-wide">
              Dervis Baygül
            </h3>
            <span className="text-gray-500 text-sm uppercase tracking-widest font-medium">
              {t('subtitle')}
            </span>
          </div>
          <div className="w-full h-px bg-white/10" />
        </div>

        <div className="flex flex-col pl-4 md:pl-20">
          {projects.map((base) => {
            const project = localize(base);
            return (
              <Link
                key={project.id}
                to={`/projekte/${project.slug}`}
                className="experience-item group relative border-b border-white/5 py-6 transition-colors duration-300 hover:bg-white/[0.025] rounded-lg px-2 md:px-4"
                aria-label={`${project.title} ${t('detailAria')}`}
              >
                <div className="grid md:grid-cols-12 gap-4 md:gap-8 items-start">
                  <div className="md:col-span-4 flex flex-col">
                    <div className="flex items-center gap-3">
                      <h3 className="text-xl font-medium text-white group-hover:text-gray-200 transition-colors">
                        {project.title}
                      </h3>
                      <SafeIcon
                        icon={FiArrowUpRight}
                        className="w-4 h-4 text-gray-600 transition-all duration-300 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1"
                      />
                    </div>
                    <p className="text-gray-500 mt-1 text-xs uppercase tracking-wider font-semibold">
                      {project.role}
                    </p>
                    <span className="text-sm font-mono text-emerald-500/80 mt-1">
                      [{project.status}]
                    </span>
                  </div>
                  <div className="md:col-span-8">
                    <p className="text-gray-400 leading-relaxed text-base font-light group-hover:text-gray-300 transition-colors duration-300">
                      {project.summary}
                    </p>
                    <span className="inline-block mt-3 text-xs uppercase tracking-[0.18em] text-gray-600 group-hover:text-white transition-colors">
                      {t('viewProject')}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
